import { Card, Spin, Tooltip } from 'antd';
import { useTheme } from '../../contexts/useTheme';
import { useStationObservations } from '../../contexts/useStationObservations';
import { useTopBarStation } from '../../contexts/useTopBarStation';

// Light intensity icon component - visualizes the intensity as a filled container
const LightIntensityIcon: React.FC<{ intensity: number | null; theme: 'light' | 'dark'; compact?: boolean }> = ({ intensity, theme, compact = false }) => {
  // Calculate fill percentage (0-100%)
  // Typical outdoor light ranges:
  // - Night/darkness: 0-10 Lx
  // - Indoor lighting: 100-500 Lx
  // - Overcast day: 1,000-10,000 Lx
  // - Full daylight (not direct sun): 10,000-25,000 Lx
  // - Direct sunlight: 50,000-100,000+ Lx
  
  const getFillPercentage = () => {
    if (intensity === null || intensity === 0) return 0;
    // Use logarithmic scale for better visualization
    // Map 0-150000 Lx to 0-100%
    const maxLux = 150000;
    const percentage = Math.min((intensity / maxLux) * 100, 100);
    return percentage;
  };

  const fillPercentage = getFillPercentage();
  const iconColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)';
  const fillColor = theme === 'dark' ? '#ffd700' : '#ffa500'; // Gold/orange color for light
  const bgColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)';

  const w = compact ? 12 : 16;
  const h = compact ? 20 : 32;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
      <rect
        x="2"
        y="2"
        width={w - 4}
        height={h - 4}
        rx="2"
        fill={bgColor}
        stroke={iconColor}
        strokeWidth="1"
      />
      {fillPercentage > 0 && (
        <rect
          x="3"
          y={3 + ((h - 6) * (100 - fillPercentage)) / 100}
          width={w - 6}
          height={((h - 6) * fillPercentage) / 100}
          rx="1"
          fill={fillColor}
          opacity="0.8"
        />
      )}
    </svg>
  );
};

interface LightIntensityProps {
  compact?: boolean;
}

export const LightIntensity: React.FC<LightIntensityProps> = ({ compact = false }) => {
  const { theme } = useTheme();
  const { currentWeather, loading, tioLoading, dataSource } = useStationObservations();
  const { topBarStationId } = useTopBarStation();

  const isLoading = dataSource === 'stations' ? loading : tioLoading;
  const intensity = currentWeather.lightIntensity;

  const getDisplayIntensity = () => {
    if (isLoading) return '...';
    if (intensity === null) return '--';
    // Format with thousands separator
    return intensity.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const card = (
    <Card
      size="small"
      style={{
        background: theme === 'dark' ? '#1f1f1f' : '#f5f5f5',
        border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #d9d9d9',
        minWidth: compact ? '80px' : '120px',
        height: compact ? '32px' : '48px',
      }}
      styles={{ body: { padding: compact ? '2px 6px' : '4px 10px', height: '100%' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '4px' : '8px', height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <LightIntensityIcon intensity={intensity} theme={theme} compact={compact} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          {!compact && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                fontSize: '9px',
                fontWeight: 500,
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                lineHeight: '1.2',
                marginBottom: '2px',
              }}
            >
              <span>Light Intensity</span>
              {dataSource === 'stations' && (
                <span style={{ opacity: 0.8 }}>{topBarStationId === 'avg' ? '*' : topBarStationId}</span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            <div
              style={{
                fontSize: compact ? '12px' : '14px',
                fontWeight: 600,
                color: theme === 'dark' ? 'white' : 'black',
                lineHeight: '1',
              }}
            >
              {getDisplayIntensity()} Lx
            </div>
            {isLoading && <Spin size="small" />}
          </div>
        </div>
      </div>
    </Card>
  );

  return compact ? <Tooltip title="Light Intensity">{card}</Tooltip> : card;
};
